import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import '../styles/About.css';

const About = ({ translations, language }) => {
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: false });

  const aboutData = {
    en: {
      title: 'About Zezo',
      subtitle: 'Digital Marketing Visionary',
      bio: 'With over 8 years of experience in digital marketing, I specialize in creating data-driven strategies that transform businesses. My expertise spans across SEO, paid advertising, social media marketing, and conversion optimization.',
      skillsTitle: 'Core Expertise',
      timelineTitle: 'Journey Timeline',
      skills: [
        { name: 'SEO & Content Strategy', level: 95 },
        { name: 'Paid Advertising (Google/Meta)', level: 92 },
        { name: 'Social Media Marketing', level: 88 },
        { name: 'Email Marketing & Automation', level: 90 },
        { name: 'Analytics & Data Analysis', level: 94 },
        { name: 'Conversion Rate Optimization', level: 87 }
      ],
      timeline: [
        { year: '2016', title: 'Started Digital Marketing Journey', desc: 'Began as a freelance digital marketer' },
        { year: '2018', title: 'Agency Growth', desc: 'Scaled to managing 50+ client accounts' },
        { year: '2020', title: 'Innovation Focus', desc: 'Pioneered AI-driven marketing strategies' },
        { year: '2023', title: 'Industry Recognition', desc: 'Top 1% digital marketing expert globally' }
      ]
    },
    ar: {
      title: 'نبذة عن زيزو',
      subtitle: 'رؤية في التسويق الرقمي',
      bio: 'مع أكثر من 8 سنوات من الخبرة في التسويق الرقمي، أتخصص في إنشاء استراتيجيات مدفوعة بالبيانات تحول الأعمال. خبرتي تشمل تحسين محركات البحث والإعلانات المدفوعة وتسويق وسائل التواصل الاجتماعي وتحسين التحويل.',
      skillsTitle: 'الخبرات الأساسية',
      timelineTitle: 'الرحلة الزمنية',
      skills: [
        { name: 'تحسين محركات البحث واستراتيجية المحتوى', level: 95 },
        { name: 'الإعلانات المدفوعة (جوجل/ميتا)', level: 92 },
        { name: 'تسويق وسائل التواصل الاجتماعي', level: 88 },
        { name: 'التسويق عبر البريد الإلكتروني والأتمتة', level: 90 },
        { name: 'التحليلات وتحليل البيانات', level: 94 },
        { name: 'تحسين معدل التحويل', level: 87 }
      ],
      timeline: [
        { year: '2016', title: 'بداية رحلة التسويق الرقمي', desc: 'بدأت كمسوق رقمي مستقل' },
        { year: '2018', title: 'نمو الوكالة', desc: 'توسعت لإدارة أكثر من 50 حساب عميل' },
        { year: '2020', title: 'التركيز على الابتكار', desc: 'ريادة استراتيجيات التسويق المدفوعة بالذكاء الاصطناعي' },
        { year: '2023', title: 'الاعتراف بالصناعة', desc: 'أفضل 1% من خبراء التسويق الرقمي عالمياً' }
      ]
    }
  };

  const data = aboutData[language];

  return (
    <section id="about" className="about-section section" ref={ref}>
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="section-title gradient-text">{data.title}</h2>
          <p className="section-subtitle">{data.subtitle}</p>
        </motion.div>

        <div className="about-content">
          <motion.div
            className="about-left"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="profile-card glass-morphism">
              <div className="profile-image-container">
                <div className="profile-image">
                  <div className="profile-placeholder">
                    <i className="fas fa-user"></i>
                  </div>
                  <div className="neon-ring"></div>
                </div>
              </div>
              <p className="bio-text">{data.bio}</p>
            </div>
          </motion.div>

          <motion.div
            className="about-right"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <div className="skills-section">
              <h3 className="skills-title">{data.skillsTitle}</h3>
              <div className="skills-grid">
                {data.skills.map((skill, index) => (
                  <motion.div
                    key={index}
                    className="skill-item"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
                  >
                    <div className="skill-header">
                      <span className="skill-name">{skill.name}</span>
                      <span className="skill-percentage">{skill.level}%</span>
                    </div>
                    <div className="skill-bar">
                      <motion.div
                        className="skill-progress"
                        initial={{ width: 0 }}
                        animate={{ width: `${skill.level}%` }}
                        transition={{ duration: 1.5, delay: 0.8 + index * 0.1 }}
                      ></motion.div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div
          className="timeline-section"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <h3 className="timeline-title">{data.timelineTitle}</h3>
          <div className="timeline">
            {data.timeline.map((item, index) => (
              <motion.div
                key={index}
                className="timeline-item"
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 1 + index * 0.2 }}
              >
                <div className="timeline-content glass-morphism">
                  <div className="timeline-year">{item.year}</div>
                  <h4 className="timeline-title-text">{item.title}</h4>
                  <p className="timeline-desc">{item.desc}</p>
                </div>
                <div className="timeline-dot"></div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;